import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { AuthProvider } from "@/auth/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomePage from "@/pages/Home";
import SearchPage from "@/pages/SearchPage";
import OffersPage from "@/pages/OffersPage";
import OfferDetailPage from "@/pages/OfferDetailPage";
import CategoriesPage from "@/pages/CategoriesPage";
import CategoryPage from "@/pages/CategoryPage";
import BrandsPage from "@/pages/BrandsPage";
import BrandPage from "@/pages/BrandPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import NotFoundPage from "@/pages/NotFoundPage";
import CouponsPage from "@/pages/CouponsPage";
import { GoogleAuthHandler } from "@/components/GoogleAuth";
import { Toaster } from "@/components/ui/sonner";

const PriceComparisonPage = lazy(() => import("@/pages/PriceComparisonPage"));
const FlightsPage = lazy(() => import("@/pages/FlightsPage"));
const HotelsPage = lazy(() => import("@/pages/HotelsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const SavedPage = lazy(() => import("@/pages/SavedPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const CardOffersPage = lazy(() => import("@/pages/CardOffersPage"));
const BlogListPage = lazy(() => import("@/pages/blog/BlogListPage"));
const BlogPostPage = lazy(() => import("@/pages/blog/BlogPostPage"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminPosts = lazy(() => import("@/pages/admin/AdminPosts"));
const AdminEditor = lazy(() => import("@/pages/admin/AdminEditor"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminAgent = lazy(() => import("@/pages/admin/AdminAgent"));

// Public site chrome (navbar/footer) is hidden inside the blog admin area.
const Chrome = () => {
    const { pathname } = useLocation();
    if (pathname.startsWith("/admin")) return null;
    return (
        <>
            <Navbar />
        </>
    );
};

const ChromeFooter = () => {
    const { pathname } = useLocation();
    if (pathname.startsWith("/admin")) return null;
    return <Footer />;
};
import AboutPage from "@/pages/About";
import FeaturesPage from "@/pages/Features";
import ContactPage from "@/pages/Contact";
import PrivacyPolicyPage from "@/pages/PrivacyPolicy";
import TermsPage from "@/pages/Terms";
import DisclaimerPage from "@/pages/Disclaimer";
import DeleteAccountPage from "@/pages/DeleteAccount";
import AffiliateDisclosurePage from "@/pages/AffiliateDisclosure";
import EditorialPolicyPage from "@/pages/EditorialPolicy";

const ScrollManager = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname]);
    return null;
};

function App() {
    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        window.__lenis = lenis;
        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    return (
        <div className="App min-h-screen bg-cream font-body text-ink antialiased">
            <div className="noise-overlay" />
            <Toaster position="top-center" richColors />
            <BrowserRouter>
                <AuthProvider>
                    <ScrollManager />
                    <Chrome />
                    <main>
                        <Suspense fallback={<div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-ink/10 border-t-saffron" /></div>}>
                        <GoogleAuthHandler>
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/search" element={<SearchPage />} />
                            <Route
                                path="/offers"
                                element={
                                    <OffersPage
                                        heading="All offers, live."
                                        eyebrow="Offers"
                                        description="Every live deal and coupon from supported platforms — filter, sort and find your sahi daam."
                                        seoTitle="Offers — SahiKhoj"
                                        seoDesc="Browse all live deals, coupons and offers on SahiKhoj from supported brands and platforms."
                                        path="/offers"
                                    />
                                }
                            />
                            <Route path="/offers/:offerId" element={<OfferDetailPage />} />
                            <Route
                                path="/coupons"
                                element={<CouponsPage />}
                            />
                            <Route
                                path="/deals"
                                element={
                                    <OffersPage
                                        preset={{ type: "deal" }}
                                        heading="Deals worth a look."
                                        eyebrow="Deals"
                                        description="Live deals from supported platforms — no code needed, just follow through to the store."
                                        seoTitle="Deals — SahiKhoj"
                                        seoDesc="Live deals from supported brands and platforms on SahiKhoj."
                                        path="/deals"
                                    />
                                }
                            />
                            <Route path="/categories" element={<CategoriesPage />} />
                            <Route path="/categories/:slug" element={<CategoryPage />} />
                            <Route path="/brands" element={<BrandsPage />} />
                            <Route path="/brands/:slug" element={<BrandPage />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/signup" element={<SignupPage />} />
                            <Route path="/price-comparison" element={<PriceComparisonPage />} />
                            <Route path="/flights" element={<FlightsPage />} />
                            <Route path="/hotels" element={<HotelsPage />} />
                            <Route path="/card-offers" element={<CardOffersPage />} />
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path="/saved" element={<SavedPage />} />
                            <Route path="/notifications" element={<NotificationsPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/features" element={<FeaturesPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                            <Route path="/terms-and-conditions" element={<TermsPage />} />
                            <Route path="/disclaimer" element={<DisclaimerPage />} />
                            <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
                            <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
                            <Route path="/delete-account" element={<DeleteAccountPage />} />
                            <Route path="/blog" element={<BlogListPage />} />
                            <Route path="/blog/category/:category" element={<BlogListPage />} />
                            <Route path="/blog/:slug" element={<BlogPostPage />} />
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminLayout />}>
                                <Route index element={<Navigate to="/admin/blog" replace />} />
                                <Route path="blog" element={<AdminPosts />} />
                                <Route path="blog/new" element={<AdminEditor />} />
                                <Route path="blog/analytics" element={<AdminAnalytics />} />
                                <Route path="blog/ai" element={<AdminAgent />} />
                                <Route path="blog/:id" element={<AdminEditor />} />
                            </Route>
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                        </GoogleAuthHandler>
                        </Suspense>
                    </main>
                    <ChromeFooter />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
